import { useState, useMemo } from 'react';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import { useMyReferralPayouts } from '@/api/account';
import { paginationView } from '@/pages/customers/pagination-summary';
import { buildPayoutColumns } from '@/pages/affiliates/detail/payouts-tab';

const PAGE_SIZE = 20;

/**
 * Employee self-view: Payouts tab.
 * Reuses column definitions from the manager payouts-tab. No expandable per-payout
 * commission sub-rows — /payouts/:id/commissions is manager-only.
 * Data comes from `/employees/current/referral-payouts`.
 */
export function ReferralPayoutsTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useMyReferralPayouts({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => buildPayoutColumns(), []);

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load payouts"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <DataTable
      columns={columns}
      data={rows}
      enableSorting={false}
      getRowId={(r) => String(r.payout_id)}
      emptyState={
        <EmptyState
          title="No payouts yet"
          description="Your payout records will appear here."
        />
      }
      footer={
        isLoading ? null : (
          <Pagination
            page={page}
            pageCount={view.pageCount}
            onPageChange={setPage}
            summary={view.summary}
          />
        )
      }
    />
  );
}
